// Reusable LocationMap component for individual client journeys
import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import { MapPinIcon } from '@heroicons/react/24/outline';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Create custom colored markers
const createColoredIcon = (color) => {
  // Create SVG for colored marker
  const svgIcon = `
    <svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
      <path fill="${color}" stroke="#fff" stroke-width="2" d="M12.5 0C5.596 0 0 5.596 0 12.5c0 12.5 12.5 28.5 12.5 28.5s12.5-16 12.5-28.5C25 5.596 19.404 0 12.5 0z"/>
      <circle cx="12.5" cy="12.5" r="6" fill="#fff"/>
    </svg>
  `;
  
  return L.divIcon({
    html: svgIcon,
    className: 'custom-colored-marker',
    iconSize: [25, 41],
    iconAnchor: [12.5, 41],
    popupAnchor: [0, -41]
  });
};

// Pre-create icons for residence (blue) and nationality (green)
const blueIcon = createColoredIcon('#3b82f6'); // blue-500
const greenIcon = createColoredIcon('#10b981'); // green-500

// Fix z-index containment for Leaflet maps
if (typeof document !== 'undefined') {
  const styleId = 'leaflet-z-index-fix';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .leaflet-map-container {
        isolation: isolate;
        position: relative;
        z-index: 0;
      }
      .leaflet-map-container .leaflet-pane {
        z-index: auto !important;
      }
      .leaflet-map-container .leaflet-tile-pane {
        z-index: 1 !important;
      }
      .leaflet-map-container .leaflet-overlay-pane {
        z-index: 4 !important;
      }
      .leaflet-map-container .leaflet-shadow-pane {
        z-index: 5 !important;
      }
      .leaflet-map-container .leaflet-marker-pane {
        z-index: 6 !important;
      }
      .leaflet-map-container .leaflet-tooltip-pane {
        z-index: 7 !important;
      }
      .leaflet-map-container .leaflet-popup-pane {
        z-index: 8 !important;
      }
      .leaflet-map-container .leaflet-control-container {
        z-index: 9 !important;
      }
    `;
    document.head.appendChild(style);
  }
}

const LocationMapComponent = ({ 
  locations = [], 
  height = "300px", 
  className = "",
  showFlow = false 
}) => {
  const [mapCenter, setMapCenter] = useState([40.4168, -3.7038]); // Default to Madrid
  const [mapZoom, setMapZoom] = useState(3);

  // Country code to coordinates mapping (ISO 3166-1 alpha-2 codes)
  const countryCoordinates = {
    'ES': [40.4637, -3.7492], // Spain
    'FR': [46.2276, 2.2137], // France
    'DE': [51.1657, 10.4515], // Germany
    'IT': [41.8719, 12.5674], // Italy
    'PT': [39.3999, -8.2245], // Portugal
    'GB': [55.3781, -3.4360], // United Kingdom
    'US': [39.8283, -98.5795], // United States
    'CA': [56.1304, -106.3468], // Canada
    'AU': [-25.2744, 133.7751], // Australia
    'NL': [52.1326, 5.2913], // Netherlands
    'BE': [50.5039, 4.4699], // Belgium
    'CH': [46.8182, 8.2275], // Switzerland
    'AT': [47.5162, 14.5501], // Austria
    'SE': [60.1282, 18.6435], // Sweden
    'NO': [60.4720, 8.4689], // Norway
    'DK': [56.2639, 9.5018], // Denmark
    'FI': [61.9241, 25.7482], // Finland
    'IE': [53.4129, -8.2439], // Ireland
    'PL': [51.9194, 19.1451], // Poland
    'CZ': [49.8175, 15.4730], // Czech Republic
    'GR': [39.0742, 21.8243], // Greece
    'RO': [45.9432, 24.9668], // Romania
    'HU': [47.1625, 19.5033], // Hungary
    'BG': [42.7339, 25.4858], // Bulgaria
    'HR': [45.1000, 15.2000], // Croatia
    'SI': [46.1512, 14.9955], // Slovenia
    'SK': [48.6690, 19.6990], // Slovakia
    'EE': [58.5953, 25.0136], // Estonia
    'LV': [56.8796, 24.6032], // Latvia
    'LT': [55.1694, 23.8813], // Lithuania
    'MX': [23.6345, -102.5528], // Mexico
    'BR': [-14.2350, -51.9253], // Brazil
    'AR': [-38.4161, -63.6167], // Argentina
    'CL': [-35.6751, -71.5430], // Chile
    'CO': [4.5709, -74.2973], // Colombia
    'PE': [-9.1900, -75.0152], // Peru
    'VE': [6.4238, -66.5897], // Venezuela
    'EC': [-1.8312, -78.1834], // Ecuador
    'UY': [-32.5228, -55.7658], // Uruguay
    'PY': [-23.4425, -58.4438], // Paraguay
    'BO': [-16.2902, -63.5887], // Bolivia
    'CR': [9.7489, -83.7534], // Costa Rica
    'PA': [8.5380, -80.7821], // Panama
    'DO': [18.7357, -70.1627], // Dominican Republic
    'CU': [21.5218, -77.7812], // Cuba
    'JM': [18.1096, -77.2975], // Jamaica
    'TR': [38.9637, 35.2433], // Turkey
    'RU': [61.5240, 105.3188], // Russia
    'CN': [35.8617, 104.1954], // China
    'JP': [36.2048, 138.2529], // Japan
    'KR': [35.9078, 127.7669], // South Korea
    'IN': [20.5937, 78.9629], // India
    'TH': [15.8700, 100.9925], // Thailand
    'VN': [14.0583, 108.2772], // Vietnam
    'PH': [12.8797, 121.7740], // Philippines
    'ID': [-0.7893, 113.9213], // Indonesia
    'MY': [4.2105, 101.9758], // Malaysia
    'SG': [1.3521, 103.8198], // Singapore
    'NZ': [-40.9006, 174.8860], // New Zealand
    'ZA': [-30.5595, 22.9375], // South Africa
    'EG': [26.0975, 30.0444], // Egypt
    'MA': [31.7917, -7.0926], // Morocco
    'TN': [33.8869, 9.5375], // Tunisia
    'DZ': [28.0339, 1.6596], // Algeria
    'IL': [31.0461, 34.8516], // Israel
    'AE': [23.4241, 53.8478], // United Arab Emirates
    'SA': [23.8859, 45.0792], // Saudi Arabia
    'QA': [25.3548, 51.1839], // Qatar
    'KW': [29.3117, 47.4818], // Kuwait
    'BH': [25.9304, 50.6378], // Bahrain
    'OM': [21.4735, 55.9754], // Oman
  };

  // Function to get country name from code
  const getCountryName = (code) => {
    const countryNames = {
      'ES': 'Spain', 'FR': 'France', 'DE': 'Germany', 'IT': 'Italy', 'PT': 'Portugal',
      'GB': 'United Kingdom', 'US': 'United States', 'CA': 'Canada', 'AU': 'Australia',
      'NL': 'Netherlands', 'BE': 'Belgium', 'CH': 'Switzerland', 'AT': 'Austria',
      'SE': 'Sweden', 'NO': 'Norway', 'DK': 'Denmark', 'FI': 'Finland',
      'IE': 'Ireland', 'PL': 'Poland', 'CZ': 'Czech Republic', 'GR': 'Greece',
      'RO': 'Romania', 'HU': 'Hungary', 'BG': 'Bulgaria', 'HR': 'Croatia',
      'SI': 'Slovenia', 'SK': 'Slovakia', 'EE': 'Estonia', 'LV': 'Latvia', 'LT': 'Lithuania',
      'MX': 'Mexico', 'BR': 'Brazil', 'AR': 'Argentina', 'CL': 'Chile', 'CO': 'Colombia',
      'PE': 'Peru', 'VE': 'Venezuela', 'EC': 'Ecuador', 'UY': 'Uruguay', 'PY': 'Paraguay',
      'BO': 'Bolivia', 'CR': 'Costa Rica', 'PA': 'Panama', 'DO': 'Dominican Republic',
      'CU': 'Cuba', 'JM': 'Jamaica', 'TR': 'Turkey', 'RU': 'Russia', 'CN': 'China',
      'JP': 'Japan', 'KR': 'South Korea', 'IN': 'India', 'TH': 'Thailand', 'VN': 'Vietnam',
      'PH': 'Philippines', 'ID': 'Indonesia', 'MY': 'Malaysia', 'SG': 'Singapore',
      'NZ': 'New Zealand', 'ZA': 'South Africa', 'EG': 'Egypt', 'MA': 'Morocco',
      'TN': 'Tunisia', 'DZ': 'Algeria', 'IL': 'Israel', 'AE': 'United Arab Emirates',
      'SA': 'Saudi Arabia', 'QA': 'Qatar', 'KW': 'Kuwait', 'BH': 'Bahrain', 'OM': 'Oman',
    };
    return countryNames[code] || code;
  };

  // Function to get coordinates for a location (country code or country name)
  const getCoordinates = (location) => {
    // First try countryCode (ISO code like 'ES', 'FR')
    if (location.countryCode) {
      const code = location.countryCode.toUpperCase();
      if (countryCoordinates[code]) {
        return countryCoordinates[code];
      }
    }
    
    // Then try country field if it's a code
    if (location.country && location.country.length === 2) {
      const code = location.country.toUpperCase();
      if (countryCoordinates[code]) {
        return countryCoordinates[code];
      }
    }
    
    // Fallback to country name lookup
    const countryName = location.country || location.city || 'Unknown';
    const coordMap = {
      'Spain': [40.4637, -3.7492],
      'France': [46.2276, 2.2137],
      'Germany': [51.1657, 10.4515],
      'Italy': [41.8719, 12.5674],
      'Portugal': [39.3999, -8.2245],
      'United Kingdom': [55.3781, -3.4360],
      'United States': [39.8283, -98.5795],
      'Canada': [56.1304, -106.3468],
      'Australia': [-25.2744, 133.7751],
      'Unknown': [40.4168, -3.7038] // Default to Madrid
    };
    
    return coordMap[countryName] || coordMap['Unknown'];
  };

  // Calculate map center and zoom to show all locations
  useEffect(() => {
    if (locations.length > 0) {
      const coords = locations.map(loc => getCoordinates(loc));
      if (coords.length === 1) {
        setMapCenter(coords[0]);
        setMapZoom(5);
      } else if (coords.length > 1) {
        // Calculate center point between all locations
        const avgLat = coords.reduce((sum, c) => sum + c[0], 0) / coords.length;
        const avgLng = coords.reduce((sum, c) => sum + c[1], 0) / coords.length;
        setMapCenter([avgLat, avgLng]);
        // Calculate zoom to fit both points
        const maxDistance = Math.max(
          ...coords.map(c1 => 
            Math.max(...coords.map(c2 => 
              Math.sqrt(Math.pow(c1[0] - c2[0], 2) + Math.pow(c1[1] - c2[1], 2))
            ))
          )
        );
        // Adjust zoom based on distance (rough calculation)
        if (maxDistance > 50) {
          setMapZoom(2);
        } else if (maxDistance > 20) {
          setMapZoom(3);
        } else if (maxDistance > 10) {
          setMapZoom(4);
        } else {
          setMapZoom(5);
        }
      }
    }
  }, [locations]);
  if (!locations || locations.length === 0) {
    return (
      <div className={`bg-muted border border-border rounded-lg p-6 text-center ${className}`} style={{ height }}>
        <MapPinIcon className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
        <p className="text-muted-foreground">No location data available</p>
      </div>
    );
  }

  return (
    <div className={`bg-card border border-border rounded-lg overflow-hidden relative ${className}`} style={{ height, zIndex: 0 }}>
      <div className="p-4 overflow-hidden relative" style={{ zIndex: 0 }}>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-foreground">Location Journey</h4>
          {showFlow && (
            <div className="flex items-center text-sm text-muted-foreground">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
              Journey Flow
            </div>
          )}
        </div>
        
        {/* Interactive Leaflet Map */}
        <div className="overflow-hidden relative" style={{ zIndex: 0 }}>
          {/* Leaflet Map */}
          <div className="border border-border rounded-lg overflow-hidden relative leaflet-map-container" style={{ height: height, position: 'relative', zIndex: 0 }}>
            <MapContainer
              center={mapCenter}
              zoom={mapZoom}
              style={{ height: '100%', width: '100%', position: 'relative' }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              {/* Draw connection line between nationality and residence if both exist */}
              {locations.length === 2 && locations.some(l => l.type === 'nationality') && locations.some(l => l.type === 'residence') && (
                <Polyline
                  positions={locations.map(loc => getCoordinates(loc))}
                  color="#3b82f6"
                  weight={3}
                  opacity={0.6}
                  dashArray="10, 5"
                />
              )}
              
              {locations.map((location, index) => {
                const coords = getCoordinates(location);
                const countryCode = location.countryCode || location.country;
                const countryName = countryCode && countryCode.length === 2 
                  ? getCountryName(countryCode.toUpperCase()) 
                  : (location.country || 'Unknown');
                
                // Use blue icon for residence, green icon for nationality
                const markerIcon = location.type === 'residence' ? blueIcon : greenIcon;
                
                return (
                  <Marker key={index} position={coords} icon={markerIcon}>
                    <Popup>
                      <div className="text-center">
                        <div className="font-semibold text-popover-foreground">{location.name}</div>
                        <div className="text-sm text-muted-foreground">{countryName}</div>
                        <div className="text-xs text-muted-foreground/70 mt-1">{location.description}</div>
                        <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-2 ${
                          location.type === 'residence' 
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100' 
                            : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100'
                        }`}>
                          {location.type === 'residence' ? 'Residence' : 'Nationality'}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationMapComponent;
